/**
 * Cross-vertical permission resolution.
 *
 * Dispatches role → permission lookups to the correct vertical source of truth
 * (features/club/permissions or features/school/permissions) based on the
 * organization type. This is the org-aware layer that the React hooks consume.
 *
 * Two families of lookup are preserved from the pre-split system:
 *  - ungated (`getRolePermissions` / `hasPermission`): role's permissions for
 *    the org type, with NO role-availability gate — historical `usePermissions`
 *    behavior.
 *  - gated (`getPermissionsForRole` / `hasPermissionInOrganization`): returns
 *    empty / false if the role isn't available for the org type — historical
 *    `useRBAC` behavior.
 */
import { Permission } from '../../../core/permissions';
import { UserRole } from '../../../types/user';
import { OrganizationType } from '../../../models/Organization';
import { CLUB_ROLE_PERMISSIONS } from '../../club/permissions';
import { SCHOOL_ROLE_PERMISSIONS } from '../../school/permissions';
import { isRoleAvailableForOrganization } from './roles';

/**
 * Get a role's permissions for an organization type (ungated by availability).
 * The maintenance role is now defined per-vertical so club and school can diverge independently.
 */
export function getRolePermissions(orgType: OrganizationType, role: UserRole): Permission[] {
  const table = (orgType === 'club' ? CLUB_ROLE_PERMISSIONS : SCHOOL_ROLE_PERMISSIONS) as Record<string, Permission[]>;
  return table[role] ?? [];
}

/**
 * Check whether a role has a permission in an organization type (ungated).
 */
export function hasPermission(orgType: OrganizationType, role: UserRole, permission: Permission): boolean {
  return getRolePermissions(orgType, role).includes(permission);
}

/**
 * Check whether a role can perform `<RESOURCE>:<ACTION>` in an org type (ungated).
 */
export function canPerformAction(orgType: OrganizationType, role: UserRole, resource: string, action: string): boolean {
  const permission = `${resource.toUpperCase()}:${action.toUpperCase()}` as Permission;
  return hasPermission(orgType, role, permission);
}

/**
 * Get a role's permissions for an org type, gated by role availability.
 * Returns [] when the role isn't available for the organization type.
 */
export function getPermissionsForRole(role: UserRole, organizationType: OrganizationType): Permission[] {
  if (!isRoleAvailableForOrganization(role, organizationType)) return [];
  return getRolePermissions(organizationType, role);
}

/**
 * Check a permission in org context, gated by role availability.
 * Returns false when the role isn't available for the organization type.
 */
export function hasPermissionInOrganization(
  userRole: UserRole,
  organizationType: OrganizationType,
  permission: Permission
): boolean {
  if (!isRoleAvailableForOrganization(userRole, organizationType)) return false;
  return getRolePermissions(organizationType, userRole).includes(permission);
}
